import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Home, Upload, FileText, Plus } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "heylpmate_partner_token";

function makeApi(token) {
  const h = { Authorization: `Bearer ${token}` };
  return {
    get: (p) => axios.get(`${API}${p}`, { headers: h }),
    post: (p, b = {}) => axios.post(`${API}${p}`, b, { headers: h }),
  };
}

export default function Partner() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState(null);
  const [listings, setListings] = useState([]);
  const [tab, setTab] = useState("mine");
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/flatmate/settings`).then((r) => setSettings(r.data.settings));
    const api = makeApi(token);
    api.get("/partner/me").then((r) => setMe(r.data.partner)).catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); });
    api.get("/partner/listings").then((r) => setListings(r.data.listings || []));
  }, [token]);

  if (!token) return <PartnerLogin onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;

  const api = makeApi(token);
  const refresh = () =>
    api.get("/partner/listings").then((r) => setListings(r.data.listings || []));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setMe(null); };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 pt-8 pb-24" data-testid="partner-portal">
      <div className="flex justify-between items-start">
        <div>
          <div className="chip mb-2" style={{ borderColor: "#10B981", color: "#10B981" }}>Partner portal</div>
          <h1 className="font-display text-3xl">Hi {me?.name}</h1>
          <div className="text-sm text-neutral-500">Partner code · <b>{me?.partner_code}</b> · Your uploads route to the HeylpMATE admin panel tagged with your code.</div>
        </div>
        <button onClick={logout} className="chip"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
      </div>

      <div className="mt-6 flex gap-2 flex-wrap">
        <button onClick={() => setTab("mine")} data-testid="partner-tab-mine" className={`chip ${tab === "mine" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>My listings ({listings.length})</button>
        <button onClick={() => setTab("aiextract")} data-testid="partner-tab-aiextract" className={`chip ${tab === "aiextract" ? "bg-orange-50 border-orange-500 text-orange-700" : ""}`}>✨ AI Auto-Extract</button>
        <button onClick={() => setTab("post")} data-testid="partner-tab-post" className={`chip ${tab === "post" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>+ Post listing</button>
        <button onClick={() => setTab("bulk")} data-testid="partner-tab-bulk" className={`chip ${tab === "bulk" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : ""}`}>Bulk upload</button>
      </div>

      {tab === "mine" && (
        <div className="mt-4 rounded-2xl border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
              <tr><th className="p-3 text-left">Listing</th><th className="p-3">Rent</th><th className="p-3">Enquiries</th><th className="p-3">Since</th></tr>
            </thead>
            <tbody>
              {listings.map((l) => (
                <tr key={l.id} className="border-t border-black/5">
                  <td className="p-3">
                    <div className="font-bold capitalize">{l.property_type} · {l.locality || "—"}, {l.city}</div>
                    <div className="text-[10px] text-neutral-400">{l.id.slice(0, 10)}…</div>
                  </td>
                  <td className="p-3 text-center">{l.rent_inr ? `₹${l.rent_inr}` : "On Request"}</td>
                  <td className="p-3 text-center">{l.enquiries || 0}</td>
                  <td className="p-3 text-[11px] text-neutral-500">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {listings.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-neutral-500">No listings yet — post your first one.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "post" && <PartnerPostForm api={api} settings={settings} onDone={() => { refresh(); setTab("mine"); }} />}
      {tab === "aiextract" && <PartnerAiExtractForm token={token} onDone={() => { refresh(); setTab("mine"); }} />}
      {tab === "bulk" && <PartnerBulkForm api={api} onDone={() => { refresh(); setTab("mine"); }} />}
    </div>
  );
}

function PartnerAiExtractForm({ token, onDone }) {
  const [files, setFiles] = useState([]);
  const [pageUrl, setPageUrl] = useState("");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const onPick = (fl) => {
    const items = Array.from(fl || []).filter((f) =>
      ["image/jpeg", "image/png", "image/webp"].includes(f.type));
    if (items.length === 0) { toast.error("Only JPG, PNG, WebP allowed"); return; }
    setFiles((old) => [...old, ...items].slice(0, 2));
  };
  const removeAt = (i) => setFiles((old) => old.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (files.length === 0 && !pageUrl) { toast.error("Attach up to 2 images or a page URL"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      files.slice(0, 2).forEach((f) => form.append("files", f));
      if (pageUrl) form.append("page_url", pageUrl);
      if (context) form.append("extra_context", context);
      const r = await axios.post(`${API}/partner/flatmate/auto-extract-files`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const d = r.data;
      setLast(d);
      if (d.credit_charged) {
        toast.success(`Draft submitted for admin approval · ${d.extracted_fields_count} fields · ${d.listing?.listing_code}`);
      } else {
        toast(`Only ${d.extracted_fields_count} field(s) extracted. No credit charged — try a clearer image / link.`,
              { icon: "🎁", duration: 6000 });
      }
      setFiles([]); setPageUrl(""); setContext("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed — no credit charged");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 grid lg:grid-cols-2 gap-4" data-testid="partner-ai-extract">
      <div className="rounded-2xl border p-5 bg-white" style={{ borderColor: "#00604A22" }}>
        <div className="text-xs uppercase tracking-widest" style={{ color: "#667085" }}>AI Auto-Extract</div>
        <h3 className="mt-1 font-display text-2xl" style={{ color: "#00604A" }}>
          Upload up to 2 property photos or paste a Facebook / Instagram link.
        </h3>
        <p className="text-xs mt-1" style={{ color: "#667085" }}>
          We extract owner name, mobile, city, rent, amenities → create a draft for admin approval.
          <b style={{ color: "#00604A" }}> No credit charged if extraction misses required fields.</b>
        </p>

        <div className="mt-4" data-testid="partner-ai-file-dropzone">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Property/deal images (max 2)</span>
          <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-5 cursor-pointer hover:bg-neutral-50 transition"
                 style={{ borderColor: "#00604A55" }}>
            <input data-testid="partner-ai-file-input" type="file" accept="image/jpeg,image/png,image/webp"
                   multiple className="hidden"
                   onChange={(e) => onPick(e.target.files)} />
            <Upload className="w-5 h-5 mb-1.5" style={{ color: "#00604A" }} />
            <div className="text-sm font-bold" style={{ color: "#00604A" }}>Click to upload · JPG / PNG / WebP</div>
            <div className="text-[10px]" style={{ color: "#667085" }}>Max 2 files · up to 5 MB each</div>
          </label>
          {files.length > 0 && (
            <div className="mt-3 flex gap-2">
              {files.map((f, i) => (
                <div key={i} className="relative rounded-xl border overflow-hidden" style={{ borderColor: "#E4E7E5" }}>
                  <img src={URL.createObjectURL(f)} alt="" className="w-24 h-20 object-cover" />
                  <button onClick={() => removeAt(i)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full text-white flex items-center justify-center"
                          style={{ background: "#DC2626" }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="block mt-3">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Web page URL (Facebook / Instagram / any)</span>
          <input data-testid="partner-ai-page-url"
                 value={pageUrl} onChange={(e) => setPageUrl(e.target.value)}
                 placeholder="https://www.facebook.com/marketplace/item/…"
                 className="mt-1 w-full border-2 rounded-2xl px-3 py-2 outline-none text-sm"
                 style={{ borderColor: "#E4E7E5" }} />
        </label>

        <label className="block mt-3">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Extra context (optional)</span>
          <input value={context} onChange={(e) => setContext(e.target.value)}
                 placeholder="Any hint — e.g. city, price note, tenant preference"
                 className="mt-1 w-full border-2 rounded-2xl px-3 py-2 outline-none text-sm"
                 style={{ borderColor: "#E4E7E5" }} />
        </label>

        <button data-testid="partner-ai-submit" onClick={submit} disabled={busy}
                className="mt-4 w-full py-3 rounded-full font-bold text-white shadow-md transition"
                style={{ background: busy ? "#94A3B8" : "#F45118" }}>
          {busy ? "Extracting…" : "Extract & Submit for approval"}
        </button>
      </div>

      <div className="rounded-2xl border p-5 bg-white" style={{ borderColor: "#E4E7E5" }}>
        <div className="text-xs uppercase tracking-widest" style={{ color: "#667085" }}>Last submission</div>
        {!last && <div className="text-sm mt-2" style={{ color: "#667085" }}>Your last draft will appear here.</div>}
        {last?.listing && (
          <div className="mt-2 space-y-2 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="chip">{last.listing.status}</span>
              <span className="chip text-[10px]">{last.listing.listing_code}</span>
              {last.credit_charged
                ? <span className="chip text-[10px]" style={{ color: "#00604A" }}>💳 {last.extracted_fields_count} fields</span>
                : <span className="chip text-[10px]" style={{ color: "#F45118" }}>🎁 No credit charged</span>}
            </div>
            <div><b>City:</b> {last.listing.city || "—"} · <b>Locality:</b> {last.listing.locality || "—"}</div>
            <div><b>Type:</b> {last.listing.property_type} · <b>Rent:</b> ₹{last.listing.rent_inr || "—"}</div>
            <div><b>Owner:</b> {last.listing.owner_name || "—"} · <b>Mobile:</b> {last.listing.mobile || "—"}</div>
            {last.listing.description && <div className="text-xs" style={{ color: "#667085" }}>{last.listing.description}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function PartnerLogin({ onLogin }) {
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/partner/login`, { partner_code: code, password });
      toast.success(`Welcome ${r.data.partner.name}`);
      onLogin(r.data.token);
    } catch (er) { toast.error(er?.response?.data?.detail || "Login failed"); setBusy(false); }
  };
  return (
    <div className="min-h-[80vh] grid place-items-center px-5" data-testid="partner-login">
      <div className="w-full max-w-md">
        <div className="chip mb-3" style={{ borderColor: "#10B981", color: "#10B981" }}>Partner login</div>
        <h1 className="font-display text-4xl font-bold">Sign in as Partner</h1>
        <p className="text-sm text-neutral-500 mt-2">Use the partner code + password shared by HeylpMATE admin.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input required placeholder="Partner code" value={code} onChange={(e) => setCode(e.target.value)}
                 data-testid="partner-code"
                 className="w-full rounded-2xl border border-black/10 p-3" />
          <input required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                 data-testid="partner-password"
                 className="w-full rounded-2xl border border-black/10 p-3" />
          <button data-testid="partner-login-btn" disabled={busy}
                  className="btn-primary rounded-full text-white px-6 py-3 w-full font-bold">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PartnerPostForm({ api, settings, onDone }) {
  const [form, setForm] = useState({
    city: "", property_type: "flat", locality: "", rent_inr: "", room_type: "", furnished: "",
    owner_name: "", mobile: "", description: "", photos: "", great_deal: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.city) return toast.error("City is required");
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.rent_inr) payload.rent_inr = parseInt(payload.rent_inr); else delete payload.rent_inr;
      ["locality", "room_type", "furnished", "owner_name", "mobile", "description"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      payload.photos = payload.photos ? payload.photos.split(",").map((s) => s.trim()).filter(Boolean) : [];
      await api.post("/partner/listings/free", payload);
      toast.success("Listing posted — routed to HeylpMATE admin");
      onDone();
    } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 rounded-2xl border border-black/10 bg-white p-4 grid md:grid-cols-3 gap-2 text-sm" data-testid="partner-post-form">
      <select required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl border border-black/10 p-2">
        <option value="">City *</option>
        {(settings?.cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
      <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="rounded-xl border border-black/10 p-2">
        <option value="flat">Flat</option><option value="room">Room</option><option value="pg">PG</option><option value="studio">Studio</option>
      </select>
      <input placeholder="Locality" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} className="rounded-xl border border-black/10 p-2" />
      <input placeholder="Rent ₹" type="number" value={form.rent_inr} onChange={(e) => setForm({ ...form, rent_inr: e.target.value })} className="rounded-xl border border-black/10 p-2" />
      <select value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} className="rounded-xl border border-black/10 p-2">
        <option value="">Room type</option><option value="single">Single</option><option value="shared">Shared</option><option value="entire_flat">Entire flat</option>
      </select>
      <select value={form.furnished} onChange={(e) => setForm({ ...form, furnished: e.target.value })} className="rounded-xl border border-black/10 p-2">
        <option value="">Furnished</option><option value="fully">Fully</option><option value="semi">Semi</option><option value="unfurnished">Unfurnished</option>
      </select>
      <input placeholder="Owner name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-xl border border-black/10 p-2" />
      <input placeholder="Owner mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-xl border border-black/10 p-2" />
      <input placeholder="Photo URLs (comma-sep)" value={form.photos} onChange={(e) => setForm({ ...form, photos: e.target.value })} className="rounded-xl border border-black/10 p-2" />
      <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border border-black/10 p-2 md:col-span-3" rows="2" />
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={form.great_deal} onChange={(e) => setForm({ ...form, great_deal: e.target.checked })} /> Great Deal
      </label>
      <div className="md:col-span-2 text-right">
        <button disabled={busy || !form.city} data-testid="partner-submit"
                className="btn-primary rounded-full text-white px-6 py-2 disabled:opacity-50 inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> {busy ? "Posting…" : "Post listing"}
        </button>
      </div>
    </form>
  );
}

function PartnerBulkForm({ api, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseXlsx = async (file) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    if (rows.length === 0) return toast.error("Sheet is empty");
    setBusy(true);
    try {
      const r = await api.post("/partner/listings/bulk", { rows });
      setResult(r.data);
      toast.success(`Uploaded — ${r.data.created} listings from ${file.name}`);
      if (r.data.created > 0) onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const run = async () => {
    if (!text.trim()) return toast.error("Paste CSV rows first");
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return toast.error("Need header + at least 1 row");
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => { if (cells[i]) obj[h] = cells[i]; });
      return obj;
    });
    setBusy(true);
    try {
      const r = await api.post("/partner/listings/bulk", { rows });
      setResult(r.data);
      toast.success(`Uploaded — ${r.data.created} listings`);
      if (r.data.created > 0) onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-black/10 bg-white p-4 space-y-4" data-testid="partner-bulk-form">
      <div>
        <div className="font-display text-lg mb-1">Upload .xlsx or .csv file</div>
        <p className="text-xs text-neutral-500 mb-2">Drop a spreadsheet from Excel/Google Sheets. First row must be headers.</p>
        <input type="file" accept=".xlsx,.xls,.csv"
               data-testid="partner-xlsx-file"
               onChange={(e) => { const f = e.target.files?.[0]; if (f) parseXlsx(f); e.target.value = ""; }}
               className="block text-sm" disabled={busy} />
      </div>
      <div className="border-t pt-4">
        <div className="font-display text-lg mb-1">Or paste CSV rows</div>
        <p className="text-xs text-neutral-500 mb-2">Paste CSV: 1st row = headers. Only <b>city</b> + <b>property_type</b> are required.</p>
        <textarea rows="6" value={text} onChange={(e) => setText(e.target.value)}
                  data-testid="partner-bulk-text"
                  placeholder="city,property_type,locality,rent_inr,owner_name,mobile"
                  className="w-full rounded-xl border border-black/10 p-3 font-mono text-xs" />
        <button data-testid="partner-bulk-btn" onClick={run} disabled={busy}
                className="mt-2 btn-primary rounded-full text-white px-6 py-2 inline-flex items-center gap-1 disabled:opacity-50">
          <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload CSV"}
        </button>
      </div>
      {result && (
        <div className="text-xs">
          <div className="text-emerald-700 font-bold">Created: {result.created}</div>
          {result.errors?.length > 0 && (
            <ul className="list-disc pl-5 text-red-600">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
        </div>
      )}
    </div>
  );
}
